import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { db } from '../config/firebase';

const useDogBiteReports = (filters = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const prevFiltersRef = useRef(null);
  const isMountedRef = useRef(true);

  // Manual refetch function
  const refetch = useCallback(() => {
    prevFiltersRef.current = null; // Reset to force refetch
    setRefreshKey(prev => prev + 1);
  }, []);

  const district = filters?.district || '';
  const tahsil = filters?.tahsil || '';
  const uc = filters?.uc || '';
  const sex = filters?.sex || '';
  const searchQuery = filters?.searchQuery || '';
  const startDate = filters?.startDate || '';
  const endDate = filters?.endDate || '';
  const status = filters?.status || '';

  useEffect(() => {
    isMountedRef.current = true;

    const filterKey = JSON.stringify({ district, tahsil, uc, sex, searchQuery, startDate, endDate, status });

    if (prevFiltersRef.current === filterKey) {
      return;
    }
    prevFiltersRef.current = filterKey;

    const fetchReports = async () => {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const reportsRef = collection(db, 'BiteIncidentReports');

        // Build server-side query conditions
        const conditions = [];
        if (district) conditions.push(where('district', '==', district));
        if (tahsil) conditions.push(where('tahsil', '==', tahsil));

        const q = query(reportsRef, ...conditions, limit(1000));

        const snapshot = await getDocs(q);

        let reports = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || (d.data().createdAt ? new Date(d.data().createdAt) : null),
          incidentDateTime: d.data().incidentDateTime?.toDate?.() || (d.data().incidentDateTime ? new Date(d.data().incidentDateTime) : null),
        }));

        // Sort by createdAt descending (client-side to avoid index requirement)
        reports.sort((a, b) => {
          const dateA = a.createdAt || 0;
          const dateB = b.createdAt || 0;
          return dateB - dateA;
        });

        // Apply remaining client-side filters
        let filtered = reports;

        // Search filter
        if (searchQuery) {
          const queryTerm = searchQuery.toLowerCase();
          filtered = filtered.filter(report =>
            (report.patientName?.toLowerCase().includes(queryTerm)) ||
            (report.description?.toLowerCase().includes(queryTerm)) ||
            (report.placeOfIncident?.toLowerCase().includes(queryTerm))
          );
        }

        // UC filter - Robust matching
        if (uc) {
          const normalizedFilter = uc.toLowerCase().trim().replace(/\s+/g, '');
          filtered = filtered.filter(report => {
            const ucFields = [
              report.ucMcNumber,
              report.uc,
              report.ucName,
              report.ucId,
              report.hospitalUc,
              report.unionCouncil,
              report.ucMcType,
              report.patientUc
            ];

            return ucFields.some(field => {
              if (!field) return false;
              const value = String(field).toLowerCase().trim().replace(/\s+/g, '');
              return value.includes(normalizedFilter) || normalizedFilter.includes(value);
            });
          });
        }

        // Sex filter
        if (sex) {
          filtered = filtered.filter(report => report.patientSex === sex);
        }

        // Date filters
        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          filtered = filtered.filter(report => {
            const reportDate = report.incidentDateTime || report.createdAt;
            return reportDate && reportDate >= start;
          });
        }

        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          filtered = filtered.filter(report => {
            const reportDate = report.incidentDateTime || report.createdAt;
            return reportDate && reportDate <= end;
          });
        }

        // Status filter (actionTaken / actionRequired)
        if (status) {
          if (status === 'actionTaken') {
            filtered = filtered.filter(report => report.actionTaken === true);
          } else if (status === 'actionRequired') {
            filtered = filtered.filter(report => !report.actionTaken);
          }
        }

        if (isMountedRef.current) {
          setData(filtered);
        }
      } catch (err) {
        console.error('Error fetching dog bite reports:', err);
        if (isMountedRef.current) {
          setError(err.message);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchReports();

    return () => {
      isMountedRef.current = false;
    };
  }, [district, tahsil, uc, sex, searchQuery, startDate, endDate, status, refreshKey]);

  // Function to fetch hospital details by ID
  const fetchHospitalById = async (hospitalId) => {
    try {
      const hospitalRef = doc(db, 'HealthcareUsers', hospitalId);
      const hospitalSnap = await getDoc(hospitalRef);

      if (hospitalSnap.exists()) {
        return {
          id: hospitalSnap.id,
          ...hospitalSnap.data(),
          createdAt: hospitalSnap.data().createdAt?.toDate?.() || null,
        };
      }
      return null;
    } catch (err) {
      console.error('Error fetching hospital:', err);
      return null;
    }
  };

  return { data, loading, error, fetchHospitalById, refetch };
};

export default useDogBiteReports;
